/*
  size-gate.js  --  "shopper must actively choose a SIZE"
  ---------------------------------------------------------------------------
  WHAT PROBLEM THIS SOLVES
  On a product page the first size (Small) arrives pre-selected, so a shopper
  can press Add to Cart without ever touching the size picker and silently
  receive a Small. That causes wrong-size orders and returns.

  WHY THE SIZE ARRIVES PRE-SELECTED
  snippets/product-variant-picker.liquid ends with

      <script type="application/json" data-selected-variant>
        {{ product.selected_or_first_available_variant | json }}
      </script>

  and snippets/product-variant-options.liquid stamps `checked` onto the first
  value. Both always describe the first available variant, i.e. Small.

  WHY THIS IS JAVASCRIPT AND NOT LIQUID
  Removing `checked` in Liquid does not survive. Dawn re-renders the entire
  <variant-selects> block from that SAME Liquid on every option change
  (Section Rendering API -> assets/product-info.js -> updateOptionValues()).
  Any server-side "nothing selected" state is reprinted as "Small selected"
  the moment the shopper changes colour. The gate therefore lives in the
  browser and re-asserts itself after every re-render.

  HOW IT WORKS
  1. On load, and again after every re-render, every SIZE radio is unchecked,
     so no size is selected or highlighted. Colour is left completely alone.
  2. While no size is chosen, the Add to Cart button carries
     aria-disabled="true". assets/product-form.js already returns early from
     onSubmitHandler() when that attribute is "true", so the add is blocked
     without touching the `disabled` attribute, which Dawn manages itself for
     sold-out variants. Dawn's base.css already dims .button[aria-disabled=true].
  3. Pressing Add to Cart while locked shows "Please select a size" beside the
     size picker and scrolls it into view, instead of adding anything.
  4. The moment the shopper checks a size radio, the gate opens for that
     product and normal Dawn behaviour resumes.

  DEEP LINKS ( ?variant=123 )
  If the page is opened with an explicit ?variant= in the URL, that product
  starts UNLOCKED. Someone arriving from an ad on a specific colour/size has
  already made a deliberate choice, and blocking them would break a paid
  funnel. This is read once, at load, before any interaction, because Dawn
  rewrites the URL to include ?variant= after the first option change.

  RE-RENDER SAFETY
  Every listener is attached to `document` and the DOM is re-checked through a
  MutationObserver, so the gate survives <variant-selects> swaps, full
  product-info swaps and whole-<main> swaps. All DOM writes are idempotent,
  so re-running is free and cannot loop.

  TO REVERT
  Delete this file and remove the single <script> tag that loads it from
  snippets/buy-buttons.liquid. Nothing else was changed.
*/

(function () {
  'use strict';

  // This file can be injected more than once (Dawn re-executes <script> tags
  // that arrive through HTMLUpdateUtility.setInnerHTML). Only ever set up once.
  if (window.__sizeGateLoaded) return;
  window.__sizeGateLoaded = true;

  // ---------------------------------------------------------------------
  // Settings
  // ---------------------------------------------------------------------

  // Option names that must be actively chosen, lower-case. Colour is absent
  // on purpose: a default colour is fine, a default size is not.
  var GATED_OPTIONS = ['size'];

  var MESSAGE_TEXT = 'Please select a size';
  var LOCKED_CLASS = 'size-gate--locked';
  var LOCK_FLAG = 'data-size-gate-locked';

  // Read the deep-link decision ONCE, before anything can rewrite the URL.
  var DEEP_LINKED = /[?&]variant=/.test(window.location.search);

  // productId -> true once that product's size has been actively chosen.
  var chosen = Object.create(null);
  var seeded = false;

  // ---------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  // The size radios are named "Size-2". Note the theme's Liquid `capture`
  // leaves a trailing newline inside the name attribute, so trim() first.
  function optionBaseName(input) {
    return (input.getAttribute('name') || '').trim().replace(/-\d+$/, '').toLowerCase();
  }

  function isGatedInput(input) {
    return GATED_OPTIONS.indexOf(optionBaseName(input)) !== -1;
  }

  function gatedInputs(scope) {
    return toArray(scope.querySelectorAll('input[type="radio"][name]')).filter(isGatedInput);
  }

  // product-info.js swaps <variant-selects> by inserting the new node and
  // hiding the outgoing one for 500ms before removing it. Ignore that copy
  // when deciding where the visible picker is.
  function isStale(el) {
    var selects = el.closest ? el.closest('variant-selects') : null;
    return !!(selects && selects.style && selects.style.display === 'none');
  }

  // This theme renders each option twice: a hidden `.add-new` fieldset that
  // drives Dawn's JS, and the fieldset the shopper actually sees. Prefer the
  // visible one. (`.add-new` is hidden by CSS that only ships with the colour
  // picker, so fall back gracefully when it is not present.)
  function visibleFieldset(inputs) {
    var fieldsets = [];
    inputs.forEach(function (input) {
      if (isStale(input)) return;
      var fieldset = input.closest('fieldset');
      if (fieldset && fieldsets.indexOf(fieldset) === -1) fieldsets.push(fieldset);
    });
    var visible = fieldsets.filter(function (fieldset) {
      return !fieldset.classList.contains('add-new');
    });
    var pool = visible.length ? visible : fieldsets;
    return pool[pool.length - 1] || null;
  }

  function productIdOf(productInfo) {
    return productInfo.getAttribute('data-product-id') || 'default';
  }

  // ---------------------------------------------------------------------
  // The "Please select a size" prompt
  // ---------------------------------------------------------------------

  function ensureMessage(productInfo, inputs) {
    var target = visibleFieldset(inputs);
    if (!target) return null;

    var message = null;

    // A re-render can leave stray copies behind. Keep the one already sitting
    // directly after the visible picker, discard the rest.
    toArray(productInfo.querySelectorAll('[data-size-gate-message]')).forEach(function (el) {
      if (!message && el.previousElementSibling === target) {
        message = el;
      } else {
        el.remove();
      }
    });

    if (!message) {
      message = document.createElement('div');
      message.setAttribute('data-size-gate-message', '');
      message.className = 'size-gate__message';
      message.setAttribute('role', 'alert');
      message.textContent = MESSAGE_TEXT;
      message.hidden = true;
      target.insertAdjacentElement('afterend', message);
    }

    if (!message.id) message.id = 'size-gate-message-' + productIdOf(productInfo);
    return message;
  }

  function hideMessages(productInfo) {
    toArray(productInfo.querySelectorAll('[data-size-gate-message]')).forEach(function (el) {
      el.hidden = true;
    });
  }

  // ---------------------------------------------------------------------
  // Lock / unlock
  // ---------------------------------------------------------------------

  function lock(productInfo, inputs, button) {
    // Nothing selected, nothing highlighted. Setting .checked does not mutate
    // an attribute, so this cannot re-trigger the MutationObserver.
    inputs.forEach(function (input) {
      if (input.checked) input.checked = false;
    });

    if (!productInfo.classList.contains(LOCKED_CLASS)) {
      productInfo.classList.add(LOCKED_CLASS);
    }

    var message = ensureMessage(productInfo, inputs);

    if (button) {
      if (button.getAttribute('aria-disabled') !== 'true') {
        button.setAttribute('aria-disabled', 'true');
      }
      // Remember that WE locked it, so unlock() never clears an aria-disabled
      // that product-form.js set during an in-flight submit.
      if (button.getAttribute(LOCK_FLAG) !== 'true') {
        button.setAttribute(LOCK_FLAG, 'true');
      }
      if (message && button.getAttribute('aria-describedby') !== message.id) {
        button.setAttribute('aria-describedby', message.id);
      }
    }
  }

  function unlock(productInfo, button) {
    productInfo.classList.remove(LOCKED_CLASS);
    if (button && button.getAttribute(LOCK_FLAG) === 'true') {
      button.removeAttribute('aria-disabled');
      button.removeAttribute(LOCK_FLAG);
      button.removeAttribute('aria-describedby');
    }
    hideMessages(productInfo);
  }

  function apply(productInfo) {
    var button = productInfo.querySelector('.product-form__submit');
    var inputs = gatedInputs(productInfo);

    // No size option on this product (or nothing rendered yet): never gate.
    if (!inputs.length || chosen[productIdOf(productInfo)]) {
      unlock(productInfo, button);
      return;
    }

    lock(productInfo, inputs, button);
  }

  function applyAll() {
    var nodes = document.querySelectorAll('product-info');
    if (!nodes.length) return;

    // Honour a ?variant= deep link for whatever was on the page at load.
    if (!seeded) {
      seeded = true;
      if (DEEP_LINKED) {
        toArray(nodes).forEach(function (node) {
          chosen[productIdOf(node)] = true;
        });
      }
    }

    toArray(nodes).forEach(apply);
  }

  // ---------------------------------------------------------------------
  // Prompting
  // ---------------------------------------------------------------------

  function reveal(productInfo) {
    var inputs = gatedInputs(productInfo).filter(function (input) {
      return !isStale(input);
    });
    var message = productInfo.querySelector('[data-size-gate-message]');
    var fieldset = visibleFieldset(inputs);

    if (message) {
      // Re-stamping the text makes assistive tech announce it again on a
      // second attempt, not just the first.
      message.textContent = MESSAGE_TEXT;
      message.hidden = false;
    }

    if (fieldset && fieldset.scrollIntoView) {
      fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    var first = inputs.filter(function (input) {
      return input.closest('fieldset') === fieldset;
    })[0];
    if (first && first.focus) {
      try {
        first.focus({ preventScroll: true });
      } catch (e) {
        first.focus();
      }
    }
  }

  // ---------------------------------------------------------------------
  // Listeners (all on `document`, so they survive every re-render)
  // ---------------------------------------------------------------------

  // The shopper actively picked a size -> open the gate for that product.
  document.addEventListener(
    'change',
    function (event) {
      var input = event.target;
      if (!input || input.type !== 'radio' || !input.checked || !isGatedInput(input)) return;

      var productInfo = input.closest('product-info');
      if (!productInfo) return;

      chosen[productIdOf(productInfo)] = true;
      apply(productInfo);
    },
    true
  );

  // Block Add to Cart while locked and explain why.
  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('.product-form__submit');
      if (!button || button.getAttribute(LOCK_FLAG) !== 'true') return;

      event.preventDefault();
      event.stopPropagation();

      var productInfo = button.closest('product-info');
      if (productInfo) reveal(productInfo);
    },
    true
  );

  // Belt and braces: also catch a form submit (e.g. Enter key).
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;

      var productInfo = form.closest('product-info');
      if (!productInfo || !productInfo.classList.contains(LOCKED_CLASS)) return;

      event.preventDefault();
      event.stopPropagation();
      reveal(productInfo);
    },
    true
  );

  // ---------------------------------------------------------------------
  // Styles
  // ---------------------------------------------------------------------

  (function injectStyles() {
    if (document.getElementById('size-gate-styles')) return;
    var style = document.createElement('style');
    style.id = 'size-gate-styles';
    style.textContent = [
      '.size-gate__message{',
      '  margin: 0.6rem 0 0;',
      '  color: #b3261e;',
      '  font-size: 1.4rem;',
      '  line-height: 1.4;',
      '  font-weight: 600;',
      '}',
      '.size-gate__message[hidden]{ display: none; }',
      /* If dynamic checkout (Shop Pay etc.) is ever switched on, it would
         bypass the gate entirely, so keep it out of reach until a size
         is chosen. No effect while it is switched off. */
      '.size-gate--locked .shopify-payment-button{ display: none !important; }'
    ].join('\n');
    document.head.appendChild(style);
  })();

  // ---------------------------------------------------------------------
  // Run, and keep running after every re-render
  // ---------------------------------------------------------------------

  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      applyAll();
    });
  }

  applyAll();
  document.addEventListener('DOMContentLoaded', applyAll);
  window.addEventListener('load', applyAll);

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
